import { useState } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import { map, toLower, get, size, find, first } from "lodash"
import { Popover, List, ListItemButton, ListItemText, ListSubheader, Button, Box, Divider } from "@mui/material"
import { ArrowDropDown } from "@mui/icons-material"
import OPTooltip from "components/material/tooltip"

import "./styles.scss"

export default function ListItemDropDown({ label, value, listSubheader, options, setValue, classes, disabled, disableSubHeader, alignMenu = "right", disabledTooltip, listAction, disablePortal = false }) {
  const [anchor, setAnchor] = useState(null)

  const makeClick = (action) => (e) => {
    setAnchor(action === "open" ? e.currentTarget : null)
  }

  const makeSetValue = (id) => () => {
    setValue(id)
    setAnchor(null)
  }

  const open = Boolean(anchor)
  const singleOption = size(options) === 1
  const selectedOption = size(options) === 1 ? first(options) : find(options, ({ id }) => id === value)

  const { optionLabelOverride, primaryText } = selectedOption ?? {}
  const selectedOptionLabel = optionLabelOverride || primaryText

  let button = <Button disabled={disabled} data-test-class="list-item-dropdown" size="small" onClick={makeClick("open")} variant="text" endIcon={<ArrowDropDown />}>{selectedOptionLabel}</Button>
  if (disabledTooltip) {
    button = <OPTooltip title={disabledTooltip} placement="bottom-start"><span>{button}</span></OPTooltip>
  }

  return <>
    <Box className={classnames("generic-toggle", get(classes, "toggle"), { disabled, "single-option": singleOption })}>
      <div className="label">{label}</div>
      {!singleOption && button}
      {singleOption && <span className="single-option">{selectedOptionLabel}</span>}
    </Box>
    {!singleOption
      && <Popover
        classes={{
          paper: classnames("generic-toggle-info", get(classes, "popper")),
        }}
        open={open}
        anchorEl={anchor}
        disablePortal={disablePortal}
        onClose={makeClick("close")}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: alignMenu,
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: alignMenu,
        }}
      >
        <List className={classnames("generic-toggle-options", get(classes, "options"))}>
          {disableSubHeader ? null : <ListSubheader component="div" id="nested-list-subheader">
            {listSubheader ?? `Choose ${toLower(label)}`}<span onClick={makeClick("close")}>{listAction}</span>
          </ListSubheader>}
          {map(options, ({ id, primaryText, secondaryText, isDefault, disabled, tooltip, group }, index) => {
            const needDivider = index > 0 && options[index - 1].group !== group
            const listItem = <ListItemButton key={id} selected={value === id} disabled={disabled || false}>
              <ListItemText
                onClick={makeSetValue(id)}
                primary={primaryText}
                secondary={<>{secondaryText}{isDefault ? <i className="default">Default</i> : null}</>}
              />
            </ListItemButton>
            const content = tooltip ? <OPTooltip title={tooltip} key={id} placement="bottom-start"><span>{listItem}</span></OPTooltip> : listItem

            return needDivider ? <div key={id}><Divider />{content}</div> : content
          })}
        </List>
      </Popover>}
  </>
}

export const OPTION_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  primaryText: PropTypes.node.isRequired,
  optionLabelOverride: PropTypes.string,
  secondaryText: PropTypes.node,
  disabled: PropTypes.bool,
  tooltip: PropTypes.string,
  group: PropTypes.number,
})

ListItemDropDown.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
  listSubheader: PropTypes.node,
  options: PropTypes.arrayOf(OPTION_SHAPE),
  setValue: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    toggle: PropTypes.string,
    popover: PropTypes.string,
    list: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  alignMenu: PropTypes.oneOf(["left", "right"]),
  disabledTooltip: PropTypes.string,
  listAction: PropTypes.element,
  disablePortal: PropTypes.bool,
  disableSubHeader: PropTypes.bool,
}
